/**
 * FILE DROPZONE HOOK — Pattern generico di upload file via drop-zone
 * (click | drag&drop | keyboard Enter/Space) o bottone-only.
 *
 * Cosa fa il hook:
 *  - espone gli handler per drop-zone (`onDragOver`, `onDragLeave`,
 *    `onDrop`, `onKeyDown`, `onClick`) e per l'`<input type="file">`
 *    nascosto (`onInputChange` + `inputRef`);
 *  - valida il batch all-or-nothing via `isAllowed` (default: accetta
 *    tutto). Se anche solo un file fallisce, mostra `invalidMessageKey`
 *    via toast e non fa alcuna upload;
 *  - invoca `uploadFile(file)` una alla volta e conta gli `ok`: a fine
 *    batch mostra success toast (con `{{count}}`) se almeno uno e'
 *    riuscito, error toast se ce n'e' almeno uno fallito;
 *  - espone `dragOver` e `uploading` per CSS class / aria.
 *
 * Il consumer decide quali handler agganciare: una drop-zone completa
 * usa tutti; un bottone che apre file chooser usa solo `onClick` +
 * `onInputChange` + `inputRef`.
 *
 * Il hook e' agnostico al dominio: non sa se il file diventa un
 * QuotationFile, un avatar, o altro. La callback `uploadFile` e'
 * responsabile di persistere e deve ritornare `true` se l'upload
 * e' riuscito.
 */

import { useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './useToast';

/**
 * Helper standard per la prop `isAllowed` di `useFileDropzone`: un file
 * e' ammesso se il suo MIME e' nella lista `mimes` oppure se il suffisso
 * del nome (lowercase) termina con una delle `extensions` (inclusive del
 * punto, es. `.pdf`).
 *
 * Il fallback sull'estensione e' importante: alcuni browser (tipicamente
 * il drop-from-filesystem su Windows) arrivano con `file.type` vuoto. Il
 * doppio check evita falsi rifiuti. Per MIME-only o extension-only
 * basta passare un array vuoto all'altro.
 */
export function matchesMimeOrExtension(
  file: File,
  accept: { mimes?: readonly string[]; extensions?: readonly string[] },
): boolean {
  const mime = file.type;
  if (mime && accept.mimes && accept.mimes.includes(mime)) return true;
  if (accept.extensions && accept.extensions.length > 0) {
    const name = file.name.toLowerCase();
    return accept.extensions.some((ext) => name.endsWith(ext));
  }
  return false;
}

export interface UseFileDropzoneOptions {
  /**
   * Upload di un singolo file. Ritorna `true` se l'upload e' riuscito,
   * `false` in caso di errore recuperato (il hook conta gli ok per il
   * toast). Eccezioni non recuperate sono catturate dal hook e
   * considerate come fail.
   */
  uploadFile: (file: File) => Promise<boolean>;
  /**
   * Validazione per-file. Se almeno un file del batch fallisce, niente
   * viene caricato e si mostra `invalidMessageKey`. Default: accetta tutto.
   */
  isAllowed?: (file: File) => boolean;
  /** Chiave i18n del toast quando `isAllowed` fallisce. Ignorata se `isAllowed` non e' passato. */
  invalidMessageKey?: string;
  /**
   * Chiave i18n (con interpolazione `{{count}}`) per il toast di success
   * a fine batch. Se omessa, il toast di successo non viene mostrato.
   */
  successMessageKey?: string;
  /**
   * Chiave i18n per il toast di errore a fine batch quando uno o piu'
   * file sono falliti. Se omessa, il toast di errore non viene mostrato.
   */
  errorMessageKey?: string;
  /**
   * Se `false`, click/keyboard/drop sono no-op (utile per disabilitare
   * la zona esternamente). Default: `true`.
   */
  enabled?: boolean;
}

export function useFileDropzone(opts: UseFileDropzoneOptions) {
  const toast = useToast();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const enabled = opts.enabled ?? true;

  async function uploadBatch(files: File[]) {
    if (files.length === 0) return;
    if (opts.isAllowed && !files.every(opts.isAllowed)) {
      if (opts.invalidMessageKey) {
        toast({ severity: 'error', message: t(opts.invalidMessageKey) });
      }
      return;
    }
    setUploading(true);
    let okCount = 0;
    try {
      for (const file of files) {
        try {
          const ok = await opts.uploadFile(file);
          if (ok) okCount++;
        } catch {
          // Eccezioni del consumer vengono degradate a fail: il toast
          // di errore a fine batch copre questo caso.
        }
      }
      if (okCount > 0 && opts.successMessageKey) {
        toast({ severity: 'success', message: t(opts.successMessageKey, { count: okCount }) });
      }
      if (okCount < files.length && opts.errorMessageKey) {
        toast({ severity: 'error', message: t(opts.errorMessageKey) });
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    if (!enabled) return;
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave() {
    setDragOver(false);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    if (!enabled) return;
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) void uploadBatch(Array.from(e.dataTransfer.files));
  }
  function onClick() {
    if (!enabled || uploading) return;
    inputRef.current?.click();
  }
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!enabled || uploading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) void uploadBatch(Array.from(e.target.files));
  }

  return { inputRef, dragOver, uploading, onDragOver, onDragLeave, onDrop, onClick, onKeyDown, onInputChange };
}
